import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import SweetAlert2 from "react-sweetalert2";
import { Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";

import InfoTab from "./components/InfoTab";
import SecurityTab from "./components/SecurityTab";
import SettingsTab from "./components/SettingsTab";

const Profile = () => {
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileReload, setProfileReload] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [swalProps, setSwalProps] = useState<any>({});

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  const fetchUserProfile = async (
    isInit: boolean = true,
    isUpdate: boolean = false,
    signal?: AbortSignal
  ) => {
    try {
      if (isInit) {
        setIsFetching(true);
      }

      if (isUpdate) {
        setProfileReload(true);
      }

      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/user/get-user-profile`,
        { signal }
      );

      const userData = response.data.user;

      setUserProfile(userData);
      setIsFetching(false);
      setProfileReload(false);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    const signal = getAbortSignal();
    fetchUserProfile(true, false, signal);
  }, []); // eslint-disable-line

  const reloadProfile = async () => {
    fetchUserProfile(false, true);
  };

  // Define tab configuration
  const tabConfig = useMemo(() => [
    { label: "Info", show: true },
    { label: "Security", show: true },
    { label: "Settings", show: true },
  ], []);

  // Filter visible tabs (currently all are visible, but ready for permissions)
  const visibleTabs = useMemo(() => {
    return tabConfig.filter(tab => tab.show);
  }, [tabConfig]);

  // Render tab content based on label
  const renderTabContent = (label: string) => {
    switch (label) {
      case "Info":
        return (
          <InfoTab
            setSwalProps={setSwalProps}
            isFetching={isFetching || profileReload}
            userProfile={userProfile}
          />
        );
      case "Security":
        return (
          <SecurityTab
            setSwalProps={setSwalProps}
            isFetching={isFetching}
            userProfile={userProfile}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
          />
        );
      case "Settings":
        return (
          <SettingsTab
            setSwalProps={setSwalProps}
            isFetching={isFetching}
            userProfile={userProfile}
            triggerReload={reloadProfile}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Tabs className="card mt-3 !rounded-xl">
        <TabList className="rounded-tl-custom rounded-tr-custom scrollbar-hide flex flex-wrap overflow-x-auto sm:flex-nowrap">
          {visibleTabs.map((tab, index) => (
            <Tab
              key={index}
              className={`child-tab rounded-tl-custom rounded-tr-custom sm:text-md whitespace-nowrap bg-white px-4 py-3 pt-4 text-sm text-navy-700 sm:px-6 sm:py-4 sm:pt-6 ${
                isFetching || isProcessing || !userProfile ? "pointer-events-none opacity-50" : ""
              }`}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels className="rounded-bl-custom rounded-br-custom border-t border-gray-200 bg-white">
          {visibleTabs.map((tab, index) => (
            <TabPanel key={index}>
              {renderTabContent(tab.label)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default Profile;